import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { TranscriptActions } from "../TranscriptActions";
import { ArchivedTranscript } from "../ArchivedTranscript";
import { transcriptFileName } from "../download";
import { digestTabFrom, type DigestTab } from "../digestTab";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm"; // GFM tables — without this, pipe tables render as raw text
import { agentsMarkdownComponents as markdownComponents } from "../markdown";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { AppShellV8 } from "../AppShellV8";
import { EntityPicker } from "../EntityPicker";
import { PersonFilter } from "../PersonFilter";
import { FriendlyLoading } from "../FriendlyLoading";
import { getActiveClient, useActiveClient, getCurrentMemberId } from "../activeClient";
import { useRoster, useEnsureRoster, getActiveRoster } from "../roster";
import { personFor } from "../people";
import { resolveDueIso, friendlyDue } from "../dueDates";

type Tab = DigestTab;
const TAB_LABELS: Record<Tab, string> = { summary: "Summary", "full-brief": "Full Brief", transcript: "Transcript" };
type DeliverableStatus = "draft" | "in-review" | "approved";
type ActionStatus = "done" | "in-progress" | "open";

interface Deliverable {
  id: string;
  name: string;
  type: string;
  owner: string;
  ownerColor: string;
  status: DeliverableStatus;
  due: string;
  dueIso?: string;
  difficulty: number;
  crossRef?: string;
  sourceAction?: string | null;
  isDraft?: boolean;
}

interface ActionItem {
  id: string;
  task: string;
  owner: string;
  ownerColor: string;
  status: ActionStatus;
  due: string;
  dueIso?: string;
  difficulty: "Low" | "Medium" | "High";
}

const STATUS_DOT: Record<DeliverableStatus, string> = { draft: "bg-gray-400", "in-review": "bg-amber-400", approved: "bg-emerald-400" };
const STATUS_LABEL: Record<DeliverableStatus, string> = { draft: "Draft", "in-review": "In Review", approved: "Approved" };
const ACTION_COLOR: Record<ActionStatus, string> = { done: "bg-emerald-50 text-emerald-700", "in-progress": "bg-amber-50 text-amber-700", open: "bg-gray-50 text-gray-500" };
const DIFF_COLOR: Record<string, string> = { Low: "bg-green-50 text-green-700", Medium: "bg-amber-50 text-amber-700", High: "bg-red-50 text-red-700" };
const DIFF_TOOLTIP = "Estimated effort to complete after the meeting — Low (quick turnaround), Med (focused work), High (multi-step or needs coordination).";

// Map a 1–5 deliverable difficulty score to a Low / Med / High band.
function diffBand(score: number): { label: "Low" | "Med" | "High"; color: string } {
  if (score <= 2) return { label: "Low", color: "bg-green-50 text-green-700" };
  if (score <= 3) return { label: "Med", color: "bg-amber-50 text-amber-700" };
  return { label: "High", color: "bg-red-50 text-red-700" };
}

// Solid + light bar-fill colors per difficulty band (F1 / AMB-351) — colorize the bar
// segments, not just the label. D-4: 1-2 green, 3 amber, 4-5 red. (Not used on Reviews.)
const diffFill = (score: number) => (score <= 2 ? "bg-green-500" : score <= 3 ? "bg-amber-500" : "bg-red-500");
const diffFillLight = (score: number) => (score <= 2 ? "bg-green-100" : score <= 3 ? "bg-amber-100" : "bg-red-100");
const DIFF_FILL_BY_LABEL: Record<string, string> = { Low: "bg-green-500", Medium: "bg-amber-500", High: "bg-red-500" };
const DIFF_LIGHT_BY_LABEL: Record<string, string> = { Low: "bg-green-100", Medium: "bg-amber-100", High: "bg-red-100" };

/* ── Person + client projection (operator API slugs → display) ──
   Person display + identity resolve against the SIGNED-IN tenant's roster (AMB-500) via the
   tenant-aware personFor (people.ts, reads getActiveRoster() at call time). slugForInitials /
   nameForInitials map a picked member's initials back to their id / name for persistence +
   display. Ambid (no operator roster) falls back to the built-in ROSTER unchanged. */
const slugForInitials = (initials: string): string =>
  getActiveRoster().find((m) => m.initials === initials)?.id ?? initials;
const nameForInitials = (initials: string): string =>
  getActiveRoster().find((m) => m.initials === initials)?.name ?? initials;

const _ac = getActiveClient();
const CLIENT_CONFIG: Record<string, { initials: string; name: string; color: string }> = {
  [_ac.slug]: { initials: _ac.initials, name: _ac.name, color: _ac.color },
};
const clientFor = (slug: string | null | undefined) =>
  (slug && CLIENT_CONFIG[slug]) || { initials: "—", name: slug || "", color: "bg-gray-400" };

const TYPE_LABEL: Record<string, string> = {
  email: "Email", page: "Page", social: "Social", report: "Report",
  doc: "Doc", ad: "Ad", script: "Script", data: "Data", config: "Config",
};
// Reverse maps for persisting edits back to raw frontmatter values.
const LABEL_TO_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(TYPE_LABEL).map(([slug, label]) => [label, slug])
);

/* ── API shapes ── */
interface ApiMeeting {
  id: string;
  title: string;
  client?: string | null;
  date?: string | null;
  duration?: number | null;
  participants?: string[];
  source?: string | null;
  status?: string | null;
  summary?: string;
  summaryPoints?: string[];
  keyDecisions?: string[];
  sources?: Record<string, unknown> | null;
  reviewedAt?: string | null;
  campaigns?: string[];
  body?: string;
  transcript?: string | null;
}
interface ApiDeliverable {
  id: string;
  title: string;
  deliverableType?: string;
  owner?: string | null;
  due?: string | null;
  difficulty?: number;
  status?: string | null;
  reviewState?: string | null;
  sourceAction?: string | null;
  contentFile?: string | null;
}
interface ApiAction {
  id: string;
  task: string;
  owner?: string | null;
  due?: string | null;
  status?: string | null;
  difficulty?: number;
}

/* ── Projections (API → screen shapes) ── */
// Collapse the rich status×reviewState into the digest's 3-dot display.
const toDeliverableStatus = (d: ApiDeliverable): DeliverableStatus => {
  if (d.reviewState === "approved") return "approved";
  if (d.reviewState === "in-review" || d.reviewState === "changes-requested") return "in-review";
  return "draft";
};
const fmtDueShort = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const apiToDeliverable = (d: ApiDeliverable): Deliverable => {
  const p = personFor(d.owner);
  return {
    id: d.id,
    name: d.title,
    type: TYPE_LABEL[d.deliverableType || "doc"] || "Doc",
    owner: p.initials,
    ownerColor: p.color,
    status: toDeliverableStatus(d),
    due: fmtDueShort(d.due),
    dueIso: d.due || "",
    difficulty: typeof d.difficulty === "number" ? d.difficulty : 3,
    sourceAction: d.sourceAction || null,
    isDraft: (d.contentFile || "").startsWith("First Drafts/"),
  };
};
const ACTION_DIFF: Array<"Low" | "Medium" | "High"> = ["Low", "Low", "Medium", "High", "High"];
const apiToAction = (a: ApiAction): ActionItem => {
  const p = personFor(a.owner);
  const status: ActionStatus =
    a.status === "done" ? "done" : a.status === "in-progress" ? "in-progress" : "open";
  return {
    id: a.id,
    task: a.task,
    owner: p.initials,
    ownerColor: p.color,
    status,
    due: fmtDueShort(a.due),
    dueIso: a.due || "",
    difficulty: ACTION_DIFF[Math.min(4, Math.max(0, (a.difficulty || 2) - 1))],
  };
};

interface MenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

// Shared ⋯ overflow menu — fixed/portal-positioned popover behind a click-outside backdrop so it is never clipped.
function OverflowMenu({ items, alwaysVisible = false }: { items: MenuItem[]; alwaysVisible?: boolean }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      setCoords({ top: rect.bottom + 4, left: rect.right - 160 });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        aria-label="More options"
        title="Row actions"
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-black/[0.08] bg-white text-gray-500 shadow-sm transition-all duration-150 hover:border-black/[0.16] hover:bg-gray-100 hover:text-gray-700 ${
          alwaysVisible ? "" : "opacity-0 group-hover:opacity-100"
        } ${open ? "opacity-100 border-black/[0.16] bg-gray-100 text-gray-700" : ""}`}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 6.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12 13.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM12 20.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
        </svg>
      </button>
      {open &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
            <div
              className="fixed z-[61] w-40 rounded-lg bg-white py-1 text-[13px] shadow-xl ring-1 ring-black/[0.06]"
              style={{ top: coords.top, left: coords.left }}
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((item, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); item.onClick(); }}
                  className={`flex w-full items-center px-3 py-1.5 text-left transition-all duration-150 hover:bg-gray-50 ${
                    item.destructive ? "text-red-600 hover:bg-red-50" : "text-gray-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

// Bottom toast with an Undo affordance, used by soft-delete (Archive) flows.
function UndoToast({ message, onUndo, onDismiss }: { message: string; onUndo: () => void; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div className="fixed bottom-6 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-3 rounded-lg bg-gray-900 px-4 py-2.5 text-[13px] text-white shadow-xl">
      <span>{message}</span>
      <button
        onClick={onUndo}
        className="font-semibold text-blue-300 transition-all duration-150 hover:text-blue-200 active:scale-[0.97]"
      >
        Undo
      </button>
    </div>
  );
}

const ACTION_STATUS_OPTIONS: ActionStatus[] = ["open", "in-progress", "done"];

// Trial client wired to real data via the operator API (transcript-ingest).
const DIGEST_CLIENT_SLUG = getActiveClient().slug;

export function Digest() {
  const [searchParams] = useSearchParams();
  const meetingId = searchParams.get("id");
  // GD-7: when opened from the Calendar, offer a breadcrumb back to it.
  const fromCalendar = searchParams.get("from") === "calendar";
  // C1 (AMB-345): opened from a to-do → scroll the digest to that to-do's row.
  const focusTask = searchParams.get("task");

  const [tab, setTab] = useState<Tab>(() => digestTabFrom(searchParams.get("tab")));
  const [showContext, setShowContext] = useState(false);

  // ── Real-data state (from the operator API) ──
  const [loading, setLoading] = useState(true);

  // Once data is in, scroll the focused to-do (#task-<id>) into view (C1). Runs once.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusTask || loading || focusedRef.current) return;
    const el = document.getElementById(`task-${focusTask}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      focusedRef.current = true;
    }
  }, [focusTask, loading]);
  const [notFound, setNotFound] = useState(false);
  const [meeting, setMeeting] = useState<ApiMeeting | null>(null);
  // Reactive active client for the client-switch guard. AMB-416: if you switch the active
  // client while viewing a meeting that doesn't belong to it, redirect to that client's
  // meetings dashboard (Kyle's preferred behavior) instead of stranding you on a foreign
  // meeting. Only an actual switch triggers it (not opening a foreign meeting directly), and
  // "All Clients" encompasses everything so it never redirects.
  // useActiveClient (the SCREEN hook) so a client switch re-renders Digest in place (no full
  // reload) — required for the redirect effect below to run as SPA navigation.
  const activeClient = useActiveClient();
  const navigate = useNavigate();
  const prevSlugRef = useRef(activeClient.slug);
  useEffect(() => {
    const prev = prevSlugRef.current;
    prevSlugRef.current = activeClient.slug;
    if (prev === activeClient.slug) return;          // no real switch
    if (!meeting) return;                            // nothing loaded yet
    if (activeClient.slug === "all") return;         // "all" shows every client
    if (activeClient.slug !== (meeting.client || null)) navigate("/agents/meetings");
  }, [activeClient.slug, meeting, navigate]);
  // Owner dropdown options = the registered team + this meeting's attendees (D-9), so you can
  // assign to someone who was in the room even if they're not on the roster — but never to
  // unrelated people.
  // Owner options = the signed-in tenant's roster (AMB-500), plus this meeting's attendees, so
  // a new tenant never sees the Ambid team in the picker.
  useEnsureRoster();
  const roster = useRoster();
  const ownerOptions = useMemo(() => {
    const out = roster.map((m) => ({ initials: m.initials, name: m.name, color: m.color }));
    for (const raw of meeting?.participants || []) {
      const p = personFor(raw);
      if (p.initials && p.name !== "Unassigned" && !out.some((o) => o.initials === p.initials)) {
        out.push({ initials: p.initials, name: p.name, color: p.color });
      }
    }
    return out;
  }, [meeting?.participants, roster]);

  // ── CRUD local state (seeded from the fetch) ──
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingMeta, setMeetingMeta] = useState({ date: "", time: "" });
  const [meetingArchived, setMeetingArchived] = useState(false);
  const [linkingCampaign, setLinkingCampaign] = useState(false);

  // D4: link this meeting to campaign(s) so it shows in each campaign's Recent Meetings.
  const linkMeetingToCampaigns = (ids: string[]) => {
    if (!meetingId) return;
    const merged = Array.from(new Set([...(meeting?.campaigns || []), ...ids]));
    fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: DIGEST_CLIENT_SLUG, type: "meeting", id: meetingId, fields: { campaigns: merged } }),
    }).catch(() => {});
    setMeeting((prev) => (prev ? { ...prev, campaigns: merged } : prev));
    setLinkingCampaign(false);
  };

  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [archivedDeliverables, setArchivedDeliverables] = useState<Set<string>>(new Set());

  const [actions, setActions] = useState<ActionItem[]>([]);
  const [archivedActions, setArchivedActions] = useState<Set<string>>(new Set());

  const [toast, setToast] = useState<{ message: string; undo: () => void } | null>(null);

  // Fetch the meeting + its deliverables/actions, then seed local state.
  useEffect(() => {
    if (!meetingId) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetch(`${DOCS_API_DOMAIN}/api/agents/meeting/${encodeURIComponent(meetingId)}?client=${DIGEST_CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { meeting: ApiMeeting; deliverables: ApiDeliverable[]; actions: ApiAction[] }) => {
        if (cancelled) return;
        const m = d.meeting;
        setMeeting(m);
        setMeetingTitle(m.title || "Untitled meeting");
        const dt = m.date ? new Date(m.date) : null;
        const valid = dt && !isNaN(dt.getTime());
        setMeetingMeta({
          date: valid ? dt!.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "",
          time: valid ? dt!.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "",
        });
        setDeliverables((d.deliverables || []).map(apiToDeliverable));
        setActions((d.actions || []).map(apiToAction));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  // Meeting-level edit modal
  const [editingMeeting, setEditingMeeting] = useState(false);
  const [meetingDraft, setMeetingDraft] = useState({ title: "", date: "", time: "" });

  // Deliverable edit modal
  const [editingDeliverable, setEditingDeliverable] = useState<Deliverable | null>(null);
  const [delDraft, setDelDraft] = useState<{ name: string; type: string; owner: string; due: string }>({ name: "", type: "", owner: "", due: "" });

  // Action edit modal
  const [editingAction, setEditingAction] = useState<ActionItem | null>(null);
  const [actDraft, setActDraft] = useState<{ task: string; owner: string; due: string }>({ task: "", owner: "", due: "" });

  // Action Items grouping toggle — grouped by owner (default, per GD-8) vs. flat list.
  const [groupActionsByPerson, setGroupActionsByPerson] = useState(true);
  // D1 (AMB-347): one prominent person filter scoping To-Dos + First Drafts + Documents
  // (was buried in the To-Dos header, To-Dos only). Defaults to "all" so the digest shows
  // the WHOLE meeting — drafts/documents are usually owned by whoever drafted them, so
  // defaulting to the viewer hid them (Kyle's R2 feedback). The person-first "my work"
  // view lives on Home / Calendar / My Tasks; here the filter is opt-in.
  const memberInitials = personFor(getCurrentMemberId()).initials;
  const [personFilter, setPersonFilter] = useState<string>("all");
  const matchOwner = (o: string) => personFilter === "all" || o === personFilter;

  const visibleDeliverables = deliverables.filter((d) => !archivedDeliverables.has(d.id));
  const visibleActions = actions.filter((a) => !archivedActions.has(a.id));
  // Team members present anywhere in this brief (to-dos, drafts, documents) — drives the
  // person-filter dropdown. The signed-in member is always selectable (so the default
  // value resolves even when they own nothing here).
  const filterOwners = (() => {
    const set = new Set<string>([memberInitials]);
    for (const a of visibleActions) if (a.owner && a.owner !== "?") set.add(a.owner);
    for (const d of visibleDeliverables) if (d.owner && d.owner !== "?") set.add(d.owner);
    return Array.from(set);
  })();
  const shownActions = visibleActions.filter((a) => matchOwner(a.owner));

  // Cross-reference to-dos ↔ drafts: first draft per source action, and actions by id.
  const draftForAction = new Map<string, Deliverable>();
  for (const d of visibleDeliverables) {
    if (d.sourceAction && !draftForAction.has(d.sourceAction)) draftForAction.set(d.sourceAction, d);
  }
  const actionById = new Map(visibleActions.map((a) => [a.id, a] as const));
  // Split deliverables into First Drafts (awaiting review/send) and finished Documents,
  // each scoped by the person filter (D1).
  const firstDrafts = visibleDeliverables.filter((d) => d.isDraft && matchOwner(d.owner));
  const documents = visibleDeliverables.filter((d) => !d.isDraft && matchOwner(d.owner));

  // D2 (AMB-348): jump-to-section chips at the top of the Summary tab.
  const SECTION_CHIPS = [
    { id: "sec-todos", label: "To-Dos" },
    { id: "sec-drafts", label: "First Drafts" },
    { id: "sec-docs", label: "Documents" },
    { id: "sec-decisions", label: "Key Decisions" },
  ];
  const scrollToSection = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  // ── Meeting handlers ──
  // Persist a meeting field update to the vault.
  const postMeetingUpdate = (fields: Record<string, unknown>) => {
    if (!meetingId) return;
    fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: DIGEST_CLIENT_SLUG, type: "meeting", id: meetingId, fields }),
    }).catch(() => {/* optimistic */});
  };
  const openMeetingEdit = () => {
    // Seed date/time from the raw ISO so the date/time inputs round-trip cleanly.
    const iso = meeting?.date || "";
    setMeetingDraft({ title: meetingTitle, date: iso.slice(0, 10), time: iso.slice(11, 16) });
    setEditingMeeting(true);
  };
  const saveMeetingEdit = () => {
    setMeetingTitle(meetingDraft.title);
    const fields: Record<string, unknown> = { title: meetingDraft.title };
    if (meetingDraft.date) {
      const iso = `${meetingDraft.date}T${meetingDraft.time || "00:00"}:00`;
      const dt = new Date(iso);
      if (!isNaN(dt.getTime())) {
        fields.date = iso;
        if (meeting) meeting.date = iso; // keep local raw in sync for re-edit
        setMeetingMeta({
          date: dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
          time: dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        });
      }
    }
    postMeetingUpdate(fields);
    setEditingMeeting(false);
  };
  const archiveMeeting = () => {
    setMeetingArchived(true);
    postMeetingUpdate({ status: "archived" });
    setToast({
      message: "Meeting archived · ",
      undo: () => { setMeetingArchived(false); postMeetingUpdate({ status: "processed" }); setToast(null); },
    });
  };

  // ── Digest bullet editing (summaryPoints / keyDecisions) ──
  // One bullet per line in a textarea; persist as an array to the meeting file.
  const [editSummary, setEditSummary] = useState(false);
  const [summaryText, setSummaryText] = useState("");
  const [editDecisions, setEditDecisions] = useState(false);
  const [decisionsText, setDecisionsText] = useState("");
  const saveDigestField = (field: "summaryPoints" | "keyDecisions", text: string, done: () => void) => {
    const arr = text.split("\n").map((s) => s.trim()).filter(Boolean);
    setMeeting((prev) => (prev ? { ...prev, [field]: arr } : prev));
    postMeetingUpdate({ [field]: arr });
    done();
  };

  // ── Deliverable handlers ──
  const ownerColorFor = (initials: string) => ownerOptions.find((o) => o.initials === initials)?.color || "bg-gray-400";

  // Persist a deliverable field update to the vault (optimistic; logs on failure).
  const postDeliverableUpdate = (id: string, fields: Record<string, unknown>) =>
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: DIGEST_CLIENT_SLUG, id, fields }),
    }).catch(() => {/* optimistic — UI already updated */});

  const archiveDeliverable = (d: Deliverable) => {
    setArchivedDeliverables((prev) => new Set(prev).add(d.id));
    postDeliverableUpdate(d.id, { status: "archived" }); // soft-delete per the contract
    setToast({
      message: `Archived "${d.name}" · `,
      undo: () => {
        setArchivedDeliverables((prev) => { const n = new Set(prev); n.delete(d.id); return n; });
        postDeliverableUpdate(d.id, { status: "drafted" }); // restore
        setToast(null);
      },
    });
  };
  const setDeliverableReview = (d: Deliverable) => {
    setDeliverables((prev) => prev.map((x) => (x.id === d.id ? { ...x, status: "in-review" } : x)));
    // Capture WHO submitted it so the item actually lands in the Review Center's "my"
    // slice (the old write set only reviewState, so nothing matched the my-filter and the
    // send appeared to vanish). Reviewer assignment comes from the picker (AMB-361).
    postDeliverableUpdate(d.id, { reviewState: "in-review", submittedBy: getCurrentMemberId() });
  };
  const openDeliverableEdit = (d: Deliverable) => {
    setEditingDeliverable(d);
    setDelDraft({ name: d.name, type: d.type, owner: d.owner, due: d.dueIso || "" });
  };
  const saveDeliverableEdit = () => {
    if (!editingDeliverable) return;
    const id = editingDeliverable.id;
    setDeliverables((prev) =>
      prev.map((x) =>
        x.id === id
          ? { ...x, name: delDraft.name, type: delDraft.type, owner: delDraft.owner, ownerColor: ownerColorFor(delDraft.owner), due: fmtDueShort(delDraft.due), dueIso: delDraft.due }
          : x
      )
    );
    // Convert display values back to raw frontmatter values before persisting.
    postDeliverableUpdate(id, {
      title: delDraft.name,
      deliverableType: LABEL_TO_TYPE[delDraft.type] || "doc",
      owner: slugForInitials(delDraft.owner),
      ...(delDraft.due ? { due: delDraft.due } : {}),
    });
    setEditingDeliverable(null);
  };
  // Quick owner reassign on a draft/document row (B3 / AMB-342) — a ≤2-click picker
  // mirroring reassignAction, so an unassigned or mis-owned draft is fixable in place
  // without opening the full edit modal. Persists via deliverable/update (owner is
  // allowlisted) — writable on brief-folder drafts since the AMB-339 spine fix.
  const reassignDeliverable = (id: string, owner: string) => {
    setDeliverables((prev) => prev.map((x) => (x.id === id ? { ...x, owner, ownerColor: ownerColorFor(owner) } : x)));
    postDeliverableUpdate(id, { owner: slugForInitials(owner) });
  };

  // ── Action handlers ──
  // Persist an action field update to the vault. Locally-created actions (a-new-*)
  // aren't in the vault yet, so they're skipped until the create endpoint lands.
  const persistAction = (id: string, fields: Record<string, unknown>) => {
    if (id.startsWith("a-new-")) return;
    fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: DIGEST_CLIENT_SLUG, type: "action", id, fields }),
    }).catch(() => {/* optimistic */});
  };
  const updateAction = (id: string, patch: Partial<ActionItem>) => {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };
  const changeActionStatus = (id: string, status: ActionStatus) => {
    updateAction(id, { status });
    persistAction(id, { status });
  };
  const changeActionDue = (id: string, iso: string) => {
    updateAction(id, { due: fmtDueShort(iso), dueIso: iso });
    persistAction(id, { due: iso });
  };
  const reassignAction = (id: string, owner: string) => {
    updateAction(id, { owner, ownerColor: ownerColorFor(owner) });
    persistAction(id, { owner: slugForInitials(owner) });
  };
  const archiveAction = (a: ActionItem) => {
    setArchivedActions((prev) => new Set(prev).add(a.id));
    persistAction(a.id, { status: "archived" });
    setToast({
      message: `Action archived · `,
      undo: () => {
        setArchivedActions((prev) => { const n = new Set(prev); n.delete(a.id); return n; });
        persistAction(a.id, { status: "open" });
        setToast(null);
      },
    });
  };
  const openActionEdit = (a: ActionItem) => {
    setEditingAction(a);
    setActDraft({ task: a.task, owner: a.owner, due: a.dueIso || "" });
  };
  const saveActionEdit = () => {
    if (!editingAction) return;
    const id = editingAction.id;
    const ownerSlug = slugForInitials(actDraft.owner);
    updateAction(id, { task: actDraft.task, owner: actDraft.owner, ownerColor: ownerColorFor(actDraft.owner), due: fmtDueShort(actDraft.due), dueIso: actDraft.due });
    if (id.startsWith("a-new-") && meetingId) {
      // Freshly-added action → CREATE in the vault, then swap the temp id for the real one.
      fetch(`${DOCS_API_DOMAIN}/api/agents/action/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client: DIGEST_CLIENT_SLUG,
          sourceMeeting: meetingId,
          fields: { task: actDraft.task, owner: ownerSlug, status: "open", ...(actDraft.due ? { due: actDraft.due } : {}) },
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.id) setActions((prev) => prev.map((a) => (a.id === id ? { ...a, id: d.id } : a)));
        })
        .catch(() => {/* optimistic — stays local on failure */});
    } else {
      persistAction(id, {
        task: actDraft.task,
        owner: ownerSlug,
        ...(actDraft.due ? { due: actDraft.due } : {}),
      });
    }
    setEditingAction(null);
  };
  const addAction = () => {
    const id = `a-new-${Date.now()}`;
    setActions((prev) => [
      ...prev,
      { id, task: "New action item", owner: memberInitials, ownerColor: ownerColorFor(memberInitials), status: "open", due: "—", dueIso: "", difficulty: "Medium" },
    ]);
    // open it for immediate editing
    setEditingAction({ id, task: "New action item", owner: memberInitials, ownerColor: ownerColorFor(memberInitials), status: "open", due: "—", dueIso: "", difficulty: "Medium" });
    setActDraft({ task: "New action item", owner: memberInitials, due: "" });
  };

  // Resolve an owner's display name from their initials (via the active roster), else initials.
  const ownerNameFor = (initials: string) => nameForInitials(initials);

  // A single action-item table row — reused by both the flat and grouped views.
  const renderActionRow = (a: ActionItem) => (
    <tr key={a.id} id={`task-${a.id}`} className={`group transition-all duration-150 hover:bg-gray-50/60 ${focusTask === a.id ? "bg-blue-50/70 ring-1 ring-inset ring-blue-300" : ""}`}>
      <td className="!pl-8 pr-4 py-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <button
            onClick={() => changeActionStatus(a.id, a.status === "done" ? "open" : "done")}
            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded transition-all duration-150 ${
              a.status === "done"
                ? "bg-emerald-500 text-white"
                : "border border-black/[0.12] bg-white hover:border-gray-400"
            }`}
          >
            {a.status === "done" && (
              <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            )}
          </button>
          <span className={`min-w-0 break-words [text-wrap:auto] font-medium ${a.status === "done" ? "text-gray-400 line-through" : "text-gray-900"}`}>
            {a.task}
          </span>
        </div>
        {(() => {
          // Mirror of the First Drafts "↳ from: {task}" — show the draft this to-do produced.
          const draft = draftForAction.get(a.id);
          if (!draft) return null;
          return (
            <Link to={`/agents/transcript-view?id=${encodeURIComponent(draft.id)}`} className="mt-1 ml-6 flex max-w-full items-start gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 hover:underline">
              <span className="shrink-0">→</span>
              <span className="break-words [text-wrap:auto]">{draft.name}</span>
            </Link>
          );
        })()}
      </td>
      <td className="px-4 py-3">
        {/* Owner dropdown — reassign */}
        <div className="relative inline-flex items-center">
          <span className={`pointer-events-none absolute left-2 flex h-5 w-5 items-center justify-center rounded-full ${a.ownerColor} text-[7px] font-bold text-white shadow-sm`}>{a.owner}</span>
          <select
            value={a.owner}
            onChange={(e) => reassignAction(a.id, e.target.value)}
            aria-label="Reassign owner"
            className="max-w-full cursor-pointer rounded-lg border border-transparent bg-transparent py-1 pl-9 pr-6 text-xs text-gray-600 transition-all duration-150 hover:border-black/[0.06] hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {ownerOptions.map((o) => (
              <option key={o.initials} value={o.initials}>{o.name}</option>
            ))}
          </select>
        </div>
      </td>
      <td className="px-4 py-3">
        {/* Status control */}
        <select
          value={a.status}
          onChange={(e) => changeActionStatus(a.id, e.target.value as ActionStatus)}
          aria-label="Change status"
          className={`cursor-pointer rounded-full border border-transparent px-2 py-0.5 text-[10px] font-medium transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-blue-500 ${ACTION_COLOR[a.status]}`}
        >
          {ACTION_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        {/* Editable due — free text ("Today", "This week", "Tue", a date), as the AI writes it. */}
        <input
          type="text"
          value={a.dueIso || ""}
          onChange={(e) => changeActionDue(a.id, e.target.value)}
          aria-label="Due"
          placeholder="—"
          className="w-full max-w-full rounded-lg border border-transparent bg-transparent px-2 py-0.5 text-xs text-gray-500 transition-all duration-150 hover:border-black/[0.06] hover:bg-gray-50 focus:border-blue-500 focus:bg-white focus:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-3">
        {(() => {
          // Same bar-meter legend as the First Drafts / Documents sections.
          const fill = a.difficulty === "High" ? 4 : a.difficulty === "Medium" ? 3 : 2;
          return (
            <span className="inline-flex items-center gap-2" title={`${a.difficulty} difficulty — ${DIFF_TOOLTIP}`}>
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, d) => (
                  <span key={d} className={`h-1 w-3 rounded-full ${d < fill ? (DIFF_FILL_BY_LABEL[a.difficulty] || "bg-gray-400") : (DIFF_LIGHT_BY_LABEL[a.difficulty] || "bg-gray-100")}`} />
                ))}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${DIFF_COLOR[a.difficulty]}`}>{a.difficulty === "Medium" ? "Med" : a.difficulty}</span>
            </span>
          );
        })()}
      </td>
      <td className="px-4 py-3 text-right">
        <OverflowMenu
          items={[
            { label: "Edit", onClick: () => openActionEdit(a) },
            { label: "Archive (cancel)", onClick: () => archiveAction(a), destructive: true },
          ]}
        />
      </td>
    </tr>
  );

  // A single deliverable table row — reused by the First Drafts and Documents sections.
  const renderDeliverableRow = (a: Deliverable) => {
    // A draft inherits its owner + due from the to-do it was written for (deliverables
    // carry neither in the data), so the columns aren't a bare "?" / "—".
    const linked = a.sourceAction ? actionById.get(a.sourceAction) : null;
    const ownerInitials = a.owner && a.owner !== "?" ? a.owner : linked?.owner || "?";
    const ownerColor = a.owner && a.owner !== "?" ? a.ownerColor : linked?.ownerColor || a.ownerColor;
    // AMB-419: render a REAL date with a fresh label instead of the AI's free-text string.
    // Resolve the raw due against the meeting date (deterministic), then label it relative to
    // now — so "this week" on an old meeting reads "Overdue · <date>", not a false "current".
    const rawDue = (a.dueIso && a.dueIso !== "" ? a.dueIso : linked?.dueIso) || "";
    const dueMeetingDate = meeting?.date || null;
    const dueTodayIso = new Date().toISOString().slice(0, 10);
    const dueIsoResolved = /^\d{4}-\d{2}-\d{2}/.test(rawDue) ? rawDue.slice(0, 10) : resolveDueIso(rawDue, dueMeetingDate);
    const dueText = dueIsoResolved ? friendlyDue(dueIsoResolved, dueTodayIso) : (rawDue || "—");
    const dueOverdue = dueIsoResolved ? dueIsoResolved < dueTodayIso : false;
    return (
    <tr key={a.id} className="group transition-all duration-150 hover:bg-gray-50/60">
      <td className="!pl-8 pr-4 py-3">
        <Link to={`/agents/transcript-view?id=${encodeURIComponent(a.id)}`} className="break-words [text-wrap:auto] font-medium text-blue-600 transition-all duration-150 hover:text-blue-700 hover:underline">{a.name}</Link>
        {a.crossRef && (
          <div className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">Also in: {a.crossRef}</div>
        )}
        {linked && (
          <div className="mt-1 break-words [text-wrap:auto] text-[11px] text-gray-400">↳ from: <span className="text-gray-600">{linked.task}</span></div>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">{a.type}</span>
      </td>
      <td className="px-4 py-3 align-top">
        {/* Owner dropdown — reassign in place (B3). AMB-399: stays inside its column
            (w-full + truncated label) so the control + its value never bleed into Status. */}
        <div className="relative flex w-full items-center">
          <span className={`pointer-events-none absolute left-2 z-10 flex h-5 w-5 items-center justify-center rounded-full ${ownerColor} text-[7px] font-bold text-white shadow-sm`}>{ownerInitials}</span>
          <select
            value={ownerInitials}
            onChange={(e) => reassignDeliverable(a.id, e.target.value)}
            aria-label="Reassign owner"
            className="w-full cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border border-transparent bg-transparent py-1 pl-9 pr-6 text-xs text-gray-600 transition-all duration-150 hover:border-black/[0.06] hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {ownerOptions.map((o) => (
              <option key={o.initials} value={o.initials}>{o.name}</option>
            ))}
          </select>
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[a.status]}`} />
          <span className="text-xs text-gray-600">{STATUS_LABEL[a.status]}</span>
        </div>
      </td>
      {/* AMB-399: dues wrap inside the column. AMB-419: a resolved real date + fresh label,
          overdue ones flagged amber so they read honestly. */}
      <td className={`px-4 py-3 align-top text-xs break-words [text-wrap:auto] ${dueOverdue ? "font-medium text-amber-700" : "text-gray-500"}`}>{dueText}</td>
      <td className="px-4 py-3">
        {(() => {
          const band = diffBand(a.difficulty);
          return (
            <span
              className="inline-flex items-center gap-2"
              title={`${band.label} difficulty — ${DIFF_TOOLTIP}`}
            >
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, d) => (
                  <span key={d} className={`h-1 w-3 rounded-full ${d < a.difficulty ? diffFill(a.difficulty) : diffFillLight(a.difficulty)}`} />
                ))}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${band.color}`}>{band.label}</span>
            </span>
          );
        })()}
      </td>
      <td className="py-3 pl-2 pr-5 text-right">
        {/* AMB-397: always-visible, bordered action button with right breathing room. */}
        <OverflowMenu
          alwaysVisible
          items={[
            { label: "Edit", onClick: () => openDeliverableEdit(a) },
            { label: "Send for review", onClick: () => setDeliverableReview(a) },
            { label: "Archive", onClick: () => archiveDeliverable(a), destructive: true },
          ]}
        />
      </td>
    </tr>
    );
  };

  // A full deliverable table section (header + count + caption + table) — used for both
  // First Drafts and Documents, which share identical columns.
  const renderDeliverableSection = (title: string, items: Deliverable[], caption: string, emptyText: string, anchorId?: string) => (
    <div id={anchorId} className="scroll-mt-16">
      <h2 className="mb-1 text-[13px] font-semibold text-gray-900">{title} ({items.length})</h2>
      <p className="text-[11px] text-gray-400 -mt-1 mb-2">{caption}</p>
      <div className="overflow-visible rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04]">
        <table className="w-full table-fixed text-left text-[13px]">
          <thead className="bg-gray-50/80 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            <tr>
              {/* table-fixed + sized side columns → Name takes the remainder and
                  long names wrap instead of widening the table past the viewport. */}
              <th className="!pl-8 pr-4 py-2.5">Name</th>
              <th className="px-4 py-2.5 w-[90px]">Type</th>
              <th className="px-4 py-2.5 w-[150px]">Owner</th>
              <th className="px-4 py-2.5 w-[110px]">Status</th>
              <th className="px-4 py-2.5 w-[110px]">Due</th>
              <th className="px-4 py-2.5 w-[130px]" title={DIFF_TOOLTIP}>Difficulty</th>
              <th className="py-2.5 pl-2 pr-5 w-14" />
            </tr>
          </thead>
          <tbody className="divide-y divide-black/[0.04]">
            {items.map(renderDeliverableRow)}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-[13px] text-gray-400">
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Group visible actions by owner initials, preserving first-seen order, for the grouped view.
  const actionsByOwner: Array<[string, ActionItem[]]> = (() => {
    const groups = new Map<string, ActionItem[]>();
    for (const a of shownActions) {
      const list = groups.get(a.owner) || [];
      list.push(a);
      groups.set(a.owner, list);
    }
    return Array.from(groups.entries());
  })();

  if (loading) {
    return (
      <AppShellV8 activeApp="meetings" breadcrumb={["Meetings", "Post-Meeting Digest"]}>
        <div className="flex h-full items-center justify-center">
          <FriendlyLoading message="Pulling together the meeting digest…" />
        </div>
      </AppShellV8>
    );
  }

  if (notFound || !meeting) {
    return (
      <AppShellV8 activeApp="meetings" breadcrumb={["Meetings", "Post-Meeting Digest"]}>
        <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
          <p className="text-[13px] text-gray-500">
            {meetingId ? "This meeting couldn't be found." : "No meeting selected."}
          </p>
          <Link to="/agents/meetings" className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">
            Back to Meetings
          </Link>
        </div>
      </AppShellV8>
    );
  }

  if (meetingArchived) {
    return (
      <AppShellV8 activeApp="meetings" breadcrumb={["Meetings", "Post-Meeting Digest"]}>
        <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
          <p className="text-[13px] text-gray-500">This meeting has been archived.</p>
          <Link to="/agents/meetings" className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">
            Back to Meetings
          </Link>
        </div>
        {toast && <UndoToast message={toast.message} onUndo={toast.undo} onDismiss={() => setToast(null)} />}
      </AppShellV8>
    );
  }

  // ── Derived display values from the real meeting ──
  const client = clientFor(meeting.client);
  const durationLabel = typeof meeting.duration === "number" ? `${meeting.duration} min` : "";
  const participants = (meeting.participants || []).map(personFor);
  const summaryPoints = meeting.summaryPoints || [];
  const keyDecisions = meeting.keyDecisions || [];

  const digestActions = (
    <div className="flex shrink-0 items-center gap-2">
      <button data-testid="link-campaign" onClick={() => setLinkingCampaign(true)} className="flex items-center gap-1.5 rounded-lg border border-black/[0.06] bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]">
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" /></svg>
        Link to campaign
      </button>
      <OverflowMenu
        alwaysVisible
        items={[
          { label: "Edit details", onClick: openMeetingEdit },
          { label: "Archive", onClick: archiveMeeting, destructive: true },
        ]}
      />
    </div>
  );

  return (
    <AppShellV8
      activeApp="meetings"
      breadcrumb={["Meetings", meetingTitle || "Post-Meeting Digest"]}
      headerActions={digestActions}
    >
      <div className="flex h-full flex-col overflow-auto">
        {/* AMB-416: the client-switch guard is now a redirect (see the effect above) — switching
            to a client this meeting doesn't belong to sends you to that client's meetings list. */}
        {/* Meeting header */}
        <div className="border-b border-black/[0.06] px-6 py-5">
          <div className="mb-2.5 flex items-center gap-2.5">
            <Link to="/agents/meetings" className="rounded-lg p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-50 hover:text-gray-600">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </Link>
            {/* GD-7: breadcrumb back to the Calendar when the digest was opened from it. */}
            {fromCalendar && (
              <Link to="/agents/calendar" className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-all duration-150 hover:bg-gray-50 hover:text-gray-700">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                Calendar
              </Link>
            )}
            {meeting.client ? (
              <>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full ${client.color} text-[8px] font-bold text-white shadow-sm`}>{client.initials}</span>
                <span className="text-xs font-medium text-gray-600">{client.name}</span>
              </>
            ) : (
              <span className="text-xs font-medium text-gray-400">Internal meeting</span>
            )}
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-gray-900">{meetingTitle}</h1>
          </div>
          <div className="mt-1.5 flex items-center gap-4 text-xs text-gray-400">
            <span>{[meetingMeta.date, meetingMeta.time].filter(Boolean).join(" · ")}</span>
            {durationLabel && <span>{durationLabel}</span>}
            <span className="flex items-center gap-1">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
              {visibleDeliverables.length} {visibleDeliverables.length === 1 ? "deliverable" : "deliverables"}
            </span>
            <span className="flex items-center gap-1">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {visibleActions.length} {visibleActions.length === 1 ? "action item" : "action items"}
            </span>
            {participants.length > 0 && (
              <div className="flex -space-x-1.5">
                {participants.map((p) => (
                  <span
                    key={p.initials}
                    className={`flex h-6 w-6 items-center justify-center rounded-full ${p.color} text-[8px] font-bold text-white ring-2 ring-white shadow-sm`}
                    title={p.name}
                  >
                    {p.initials}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tabs — with the jump-to-section chips + person filter inline on the
            same row, shown only on the Summary view (D1+D2 / AMB-347+348). */}
        <div className="border-b border-black/[0.06] px-6 py-2.5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex gap-1 rounded-lg bg-gray-100/70 p-0.5 w-fit">
              {(["summary", "full-brief", "transcript"] as Tab[]).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`rounded-md px-4 py-1.5 text-xs font-medium transition-all duration-150 ${
                    tab === t
                      ? "bg-gray-900 text-white shadow-sm"
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                  }`}
                >
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>
            {/* the transcript's Download / Copy land here (Kyle, Oct 28: no repeated header row) */}
            {tab === "transcript" && <span id="digest-tab-actions" className="ml-auto" />}
            {tab === "summary" && (
              // AMB-386: jump-to chips sit on the RIGHT, next to the person filter (one
              // right-aligned group), instead of left-of-tabs with the filter pushed away.
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-medium text-gray-400">Jump to</span>
                {SECTION_CHIPS.map((c) => (
                  <button key={c.id} onClick={() => scrollToSection(c.id)} className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 ring-1 ring-black/[0.06] transition-all duration-150 hover:bg-gray-200/70 hover:text-gray-800">{c.label}</button>
                ))}
                {/* AMB-387: shared person filter + consistent "Showing tasks for" label. */}
                <PersonFilter
                  value={personFilter}
                  onChange={setPersonFilter}
                  options={filterOwners.map((o) => ({ id: o, name: ownerNameFor(o) }))}
                />
              </div>
            )}
          </div>
        </div>

        {/* Summary tab */}
        {tab === "summary" && (
          <div className="flex-1 overflow-auto px-6 py-6 space-y-6">
            {/* Meeting Summary — structured digest bullets when present, else the
                full meeting brief (markdown body). */}
            <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-5">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-[13px] font-semibold text-gray-900">Meeting Summary</h2>
                {editSummary ? (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditSummary(false)} className="text-[11px] font-medium text-gray-500 hover:text-gray-700">Cancel</button>
                    <button onClick={() => saveDigestField("summaryPoints", summaryText, () => setEditSummary(false))} className="rounded-md bg-gray-900 px-2.5 py-0.5 text-[11px] font-medium text-white hover:bg-gray-800">Save</button>
                  </div>
                ) : (
                  <button onClick={() => { setSummaryText(summaryPoints.join("\n")); setEditSummary(true); }} className="text-[11px] font-medium text-gray-400 hover:text-gray-700">Edit</button>
                )}
              </div>
              {editSummary ? (
                <textarea
                  value={summaryText}
                  onChange={(e) => setSummaryText(e.target.value)}
                  placeholder="One summary point per line…"
                  className="h-40 w-full resize-y rounded-lg border border-black/[0.06] p-3 text-[13px] leading-relaxed text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              ) : summaryPoints.length > 0 ? (
                <ul className="space-y-2 text-[13px] leading-relaxed text-gray-600">
                  {summaryPoints.map((pt, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
                      {pt}
                    </li>
                  ))}
                </ul>
              ) : meeting.body ? (
                <div className="prose prose-sm max-w-none text-[13px] leading-relaxed text-gray-600 prose-headings:text-gray-900 prose-a:text-blue-600">
                  <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                    {meeting.body}
                  </Markdown>
                </div>
              ) : meeting.summary ? (
                <p className="text-[13px] leading-relaxed text-gray-600">{meeting.summary}</p>
              ) : (
                <p className="text-[13px] text-gray-400">No summary yet for this meeting.</p>
              )}
            </div>

            {/* Key Decisions — editable (one per line) */}
            <div id="sec-decisions" className="scroll-mt-16 rounded-xl bg-gray-50 border-l-2 border-gray-200 p-5">
              <div className="mb-2.5 flex items-center justify-between">
                <h3 className="text-[13px] font-semibold text-gray-900">Key Decisions</h3>
                {editDecisions ? (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditDecisions(false)} className="text-[11px] font-medium text-gray-500 hover:text-gray-700">Cancel</button>
                    <button onClick={() => saveDigestField("keyDecisions", decisionsText, () => setEditDecisions(false))} className="rounded-md bg-gray-900 px-2.5 py-0.5 text-[11px] font-medium text-white hover:bg-gray-800">Save</button>
                  </div>
                ) : (
                  <button onClick={() => { setDecisionsText(keyDecisions.join("\n")); setEditDecisions(true); }} className="text-[11px] font-medium text-gray-400 hover:text-gray-700">Edit</button>
                )}
              </div>
              {editDecisions ? (
                <textarea
                  value={decisionsText}
                  onChange={(e) => setDecisionsText(e.target.value)}
                  placeholder="One decision per line…"
                  className="h-28 w-full resize-y rounded-lg border border-black/[0.06] p-3 text-[13px] leading-relaxed text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              ) : keyDecisions.length > 0 ? (
                <ol className="space-y-1.5 text-[13px] leading-relaxed text-gray-700">
                  {keyDecisions.map((d, i) => (
                    <li key={i}>{i + 1}. {d}</li>
                  ))}
                </ol>
              ) : (
                <p className="text-[13px] text-gray-400">No key decisions recorded.</p>
              )}
            </div>

            {/* To-Dos */}
            <div id="sec-todos" className="scroll-mt-16">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-[13px] font-semibold text-gray-900">To-Dos ({shownActions.length})</h2>
                  <p className="text-[11px] text-gray-400 -mt-1 mb-2">Tasks from this meeting. The “→” link under a to-do opens the draft written for it.</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Person filter promoted to the Summary toolbar (D1); the grouping
                      toggle stays here — it only affects the To-Dos table. */}
                  {/* Grouping toggle — flat list (default) vs. grouped by owner */}
                  <div className="flex gap-0.5 rounded-lg bg-gray-100/70 p-0.5">
                    {([
                      { val: false, label: "Flat list" },
                      { val: true, label: "Group by person" },
                    ] as const).map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => setGroupActionsByPerson(opt.val)}
                        className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-all duration-150 ${
                          groupActionsByPerson === opt.val
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={addAction}
                    className="flex items-center gap-1 rounded-lg bg-gray-900 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                    New action item
                  </button>
                </div>
              </div>
              <div className="overflow-visible rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04]">
                <table className="w-full table-fixed text-left text-[13px]">
                  <thead className="bg-gray-50/80 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    <tr>
                      {/* table-fixed + sized side columns → Task takes the remainder and
                          long titles wrap instead of widening the table past the viewport. */}
                      <th className="!pl-8 pr-4 py-2.5">Task</th>
                      <th className="px-4 py-2.5 w-[170px]">Owner</th>
                      <th className="px-4 py-2.5 w-[110px]">Status</th>
                      <th className="px-4 py-2.5 w-[130px]">Due</th>
                      <th className="px-4 py-2.5 w-[140px]" title={DIFF_TOOLTIP}>Difficulty</th>
                      <th className="px-4 py-2.5 w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/[0.04]">
                    {groupActionsByPerson && personFilter === "all"
                      ? actionsByOwner.map(([owner, items]) => (
                          <Fragment key={`grp-${owner}`}>
                            {/* Owner subheading */}
                            <tr className="bg-gray-50/80">
                              <td colSpan={6} className="!pl-8 pr-4 py-1.5">
                                <div className="flex items-center gap-2">
                                  <span className={`flex h-5 w-5 items-center justify-center rounded-full ${items[0].ownerColor} text-[7px] font-bold text-white shadow-sm`}>{owner}</span>
                                  <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{ownerNameFor(owner)}</span>
                                  <span className="text-[10px] font-medium text-gray-400">({items.length})</span>
                                </div>
                              </td>
                            </tr>
                            {items.map(renderActionRow)}
                          </Fragment>
                        ))
                      : shownActions.map(renderActionRow)}
                    {/* + New action item row */}
                    <tr>
                      <td colSpan={6} className="!pl-8 pr-4 py-2">
                        <button
                          onClick={addAction}
                          className="flex items-center gap-1.5 text-[12px] font-medium text-gray-400 transition-all duration-150 hover:text-gray-700"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                          New action item
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* First Drafts */}
            {renderDeliverableSection(
              "First Drafts",
              firstDrafts,
              "Drafted assets awaiting review or sending.",
              "No drafts yet.",
              "sec-drafts"
            )}

            {/* Documents */}
            {renderDeliverableSection(
              "Documents",
              documents,
              "Finished documents and artifacts from this meeting.",
              "No documents yet.",
              "sec-docs"
            )}

            {/* Sources — provenance the agent used (only when the brief recorded it) */}
            {meeting.sources && Object.keys(meeting.sources).length > 0 && (
              <div>
                <button
                  onClick={() => setShowContext(!showContext)}
                  className="flex items-center gap-2 text-xs font-medium text-gray-400 transition-all duration-150 hover:text-gray-600"
                >
                  <svg
                    className={`h-3 w-3 transition-transform duration-150 ${showContext ? "rotate-90" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                  Sources
                </button>
                {showContext && (
                  <div className="mt-2.5 rounded-xl border-l border-black/[0.06] bg-gray-50/80 p-4 text-xs text-gray-500 space-y-1.5">
                    <p className="mb-2 text-[10px] text-gray-400">Data sources and context the agent used when processing this meeting.</p>
                    {Object.entries(meeting.sources).map(([key, val]) => {
                      const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
                      const display = Array.isArray(val) ? val.join(", ") : typeof val === "boolean" ? (val ? "Yes" : "No") : String(val);
                      if (!display) return null;
                      return (
                        <p key={key}>
                          <strong className="text-gray-600">{label}:</strong> {display}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Full Brief tab — the complete narrative brief (markdown body): bank updates,
            deep dives, artifact rationale. Nothing lost vs the structured Summary tab. */}
        {tab === "full-brief" && (
          meeting.body ? (
            // AMB-402: paper styling — gray backdrop behind a white content card, matching the KB asset view.
            <div className="flex-1 overflow-auto bg-gray-50/60 px-6 py-6">
              <div className="prose prose-sm mx-auto max-w-3xl rounded-xl bg-white px-8 py-8 text-[13px] leading-relaxed text-gray-700 shadow-sm ring-1 ring-black/[0.04] prose-headings:text-gray-900 prose-a:text-blue-600 prose-strong:text-gray-900">
                <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                  {meeting.body}
                </Markdown>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
              <p className="text-[13px] font-medium text-gray-500">No full brief for this meeting yet.</p>
              <p className="max-w-sm text-xs text-gray-400">The structured summary is on the Summary tab.</p>
            </div>
          )
        )}

        {/* Transcript tab — the same view as the live meeting (Kyle, Oct 28): the live room that
            recorded it, with its AI notes; or the stored transcript in that look. The old paper card
            is the fallback for text that isn't a speaker-by-speaker transcript. */}
        {tab === "transcript" && (
          meeting.transcript ? (
            <div className="flex min-h-0 flex-1 flex-col">
            <ArchivedTranscript meeting={meeting} fallback={
            // AMB-402: paper styling — gray backdrop behind a white content card, matching the KB asset view.
            <div className="flex-1 overflow-auto bg-gray-50/60 px-6 py-6">
              {/* one-click download / copy (Claire, Oct 28 scrum) */}
              <div className="mx-auto mb-3 flex max-w-3xl justify-end">
                <TranscriptActions fileName={transcriptFileName(meeting.title, meeting.date)} text={() => meeting.transcript || ""} />
              </div>
              <div className="prose prose-sm mx-auto max-w-3xl rounded-xl bg-white px-8 py-8 text-[13px] leading-relaxed text-gray-700 shadow-sm ring-1 ring-black/[0.04] prose-headings:text-gray-900 prose-strong:text-gray-900">
                <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                  {meeting.transcript}
                </Markdown>
              </div>
            </div>
            } />
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
              <svg className="h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
              <p className="text-[13px] font-medium text-gray-500">No transcript available</p>
              <p className="max-w-sm text-xs text-gray-400">
                There's no transcript for this meeting yet. The full summary is on the Summary tab.
              </p>
            </div>
          )
        )}

        {/* ── Edit meeting modal ── */}
        {editingMeeting && (
          <>
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px]" onClick={() => setEditingMeeting(false)} />
            <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/[0.06]">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Edit meeting</h2>
                <button onClick={() => setEditingMeeting(false)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Title</label>
                  <input type="text" value={meetingDraft.title} onChange={(e) => setMeetingDraft((d) => ({ ...d, title: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Date</label>
                    <input type="date" value={meetingDraft.date} onChange={(e) => setMeetingDraft((d) => ({ ...d, date: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Time</label>
                    <input type="time" value={meetingDraft.time} onChange={(e) => setMeetingDraft((d) => ({ ...d, time: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditingMeeting(false)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-500 transition-all duration-150 hover:bg-gray-100">Cancel</button>
                <button onClick={saveMeetingEdit} className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">Save</button>
              </div>
            </div>
          </>
        )}

        {/* ── Edit deliverable modal ── */}
        {editingDeliverable && (
          <>
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px]" onClick={() => setEditingDeliverable(null)} />
            <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/[0.06]">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Edit deliverable</h2>
                <button onClick={() => setEditingDeliverable(null)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Name</label>
                  <input type="text" value={delDraft.name} onChange={(e) => setDelDraft((d) => ({ ...d, name: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Type</label>
                    <select value={delDraft.type} onChange={(e) => setDelDraft((d) => ({ ...d, type: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                      {Object.values(TYPE_LABEL).map((label) => (<option key={label} value={label}>{label}</option>))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Due</label>
                    <input type="text" value={delDraft.due} onChange={(e) => setDelDraft((d) => ({ ...d, due: e.target.value }))} placeholder="e.g. Today, This week, Tue" className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Owner</label>
                  <select value={delDraft.owner} onChange={(e) => setDelDraft((d) => ({ ...d, owner: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                    {ownerOptions.map((o) => (<option key={o.initials} value={o.initials}>{o.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditingDeliverable(null)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-500 transition-all duration-150 hover:bg-gray-100">Cancel</button>
                <button onClick={saveDeliverableEdit} className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">Save</button>
              </div>
            </div>
          </>
        )}

        {/* ── Edit action modal ── */}
        {editingAction && (
          <>
            <div className="fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px]" onClick={() => setEditingAction(null)} />
            <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/[0.06]">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Edit action item</h2>
                <button onClick={() => setEditingAction(null)} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-500">Task</label>
                  <input type="text" value={actDraft.task} onChange={(e) => setActDraft((d) => ({ ...d, task: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Owner</label>
                    <select value={actDraft.owner} onChange={(e) => setActDraft((d) => ({ ...d, owner: e.target.value }))} className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                      {ownerOptions.map((o) => (<option key={o.initials} value={o.initials}>{o.name}</option>))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Due</label>
                    <input type="text" value={actDraft.due} onChange={(e) => setActDraft((d) => ({ ...d, due: e.target.value }))} placeholder="e.g. Today, This week, Tue" className="w-full rounded-lg border border-black/[0.06] px-3 py-2 text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditingAction(null)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-500 transition-all duration-150 hover:bg-gray-100">Cancel</button>
                <button onClick={saveActionEdit} className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">Save</button>
              </div>
            </div>
          </>
        )}

        {/* ── Undo toast ── */}
        {toast && (
          <UndoToast message={toast.message} onUndo={toast.undo} onDismiss={() => setToast(null)} />
        )}
      </div>
      {linkingCampaign && (
        <EntityPicker
          title="Link this meeting to campaign(s)"
          endpoint={`/campaigns?client=${DIGEST_CLIENT_SLUG}`}
          mapItem={(c) => ({ id: c.id, label: c.name || c.id, sublabel: c.status })}
          multiple
          selected={meeting.campaigns || []}
          confirmLabel="Link campaigns"
          onConfirm={linkMeetingToCampaigns}
          onClose={() => setLinkingCampaign(false)}
        />
      )}
    </AppShellV8>
  );
}
